/**
 * Open-access status from OpenAlex, one DOI at a time or fifty to a request.
 *
 * OpenAlex classifies every work it indexes (`open_access.oa_status`) and ranks its
 * free copies: `best_oa_location` is the one it scores best — publisher before
 * repository, published before accepted before submitted, a PDF link before none. It
 * only knows about copies it has indexed, so "closed" means "no free copy OpenAlex
 * knows of", not "no free copy exists".
 */

import { findDoi } from "@/lib/tool-kit/reference-tools/references";

export const OA_STATUSES = [
  "diamond",
  "gold",
  "hybrid",
  "bronze",
  "green",
  "closed",
] as const;

export type OaStatus = (typeof OA_STATUSES)[number];

export type OaVersion =
  | "publishedVersion"
  | "acceptedVersion"
  | "submittedVersion";

export interface OaLocation {
  landingUrl: string | null;
  pdfUrl: string | null;
  /** OpenAlex's licence id: "cc-by", "cc-by-nc-nd", "public-domain", "other-oa"… */
  license: string | null;
  version: OaVersion | null;
  /** The journal or repository that hosts it. */
  host: string | null;
  hostType: string | null;
}

export interface OaRecord {
  /** Lower-case, without a resolver prefix. */
  doi: string;
  title: string;
  year: number | null;
  status: OaStatus;
  best: OaLocation | null;
  /** Open copies OpenAlex lists, the best one included. */
  openCopies: number;
}

/** Fifty DOIs to a request: one page of an OpenAlex `filter` query. */
export const OPENALEX_BATCH = 50;
const SELECT =
  "id,doi,title,display_name,publication_year,open_access,best_oa_location,locations";
const API = "https://api.openalex.org/works";

const RE_DOI_PREFIX = /^https?:\/\/(?:dx\.)?doi\.org\//i;
/** A comma or pipe would split an OpenAlex filter value, so such DOIs go singly. */
const RE_FILTER_UNSAFE = /[,|]/;
const RE_NEWLINE = /\r?\n/;
const VERSIONS = new Set<string>([
  "publishedVersion",
  "acceptedVersion",
  "submittedVersion",
]);
const STATUS_SET = new Set<string>(OA_STATUSES);

export function normalizeDoi(raw: string): string {
  return raw.trim().replace(RE_DOI_PREFIX, "").toLowerCase();
}

export interface DoiLine {
  /** Position in the pasted text, from 0. */
  line: number;
  input: string;
  doi: string | null;
  /** Repeats a DOI from an earlier line. */
  duplicate: boolean;
}

/** One row per non-empty line: the DOI found in it (a bare DOI, a doi: or doi.org form). */
export function readDoiLines(text: string): DoiLine[] {
  const seen = new Set<string>();
  const rows: DoiLine[] = [];
  for (const [line, raw] of text.split(RE_NEWLINE).entries()) {
    const input = raw.trim();
    if (input === "") {
      continue;
    }
    const found = findDoi(input);
    const doi = found ? normalizeDoi(found) : null;
    const duplicate = doi !== null && seen.has(doi);
    if (doi) {
      seen.add(doi);
    }
    rows.push({ line, input, doi, duplicate });
  }
  return rows;
}

/** Batches for the filter endpoint, plus the DOIs that must be looked up one by one. */
export function planOpenAlex(dois: readonly string[]): {
  batches: string[][];
  singles: string[];
} {
  const filterable = dois.filter((doi) => !RE_FILTER_UNSAFE.test(doi));
  const singles = dois.filter((doi) => RE_FILTER_UNSAFE.test(doi));
  const batches: string[][] = [];
  for (let index = 0; index < filterable.length; index += OPENALEX_BATCH) {
    batches.push(filterable.slice(index, index + OPENALEX_BATCH));
  }
  return { batches, singles };
}

export function openAlexBatchUrl(dois: readonly string[]): string {
  const filter = dois.map((doi) => encodeURIComponent(doi)).join("|");
  return `${API}?filter=doi:${filter}&per-page=${OPENALEX_BATCH}&select=${SELECT}`;
}

export function openAlexWorkUrl(doi: string): string {
  return `${API}/doi:${encodeURIComponent(doi)}?select=${SELECT}`;
}

/* -------------------------------------------------------------- parsing */

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function location(raw: unknown): OaLocation | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const source = (record.source ?? {}) as Record<string, unknown>;
  const version = text(record.version);
  return {
    landingUrl: text(record.landing_page_url),
    pdfUrl: text(record.pdf_url),
    license: text(record.license),
    version: version && VERSIONS.has(version) ? (version as OaVersion) : null,
    host: text(source.display_name),
    hostType: text(source.type),
  };
}

function statusOf(raw: unknown): OaStatus {
  const status = text((raw as { oa_status?: unknown } | null)?.oa_status);
  return status && STATUS_SET.has(status) ? (status as OaStatus) : "closed";
}

/** One OpenAlex work as an open-access record; null without a DOI to key it by. */
export function parseOpenAlexWork(raw: unknown): OaRecord | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const work = raw as Record<string, unknown>;
  const doi = text(work.doi);
  if (!doi) {
    return null;
  }
  const locations = Array.isArray(work.locations) ? work.locations : [];
  const year = Number(work.publication_year);
  return {
    doi: normalizeDoi(doi),
    title: text(work.display_name) ?? text(work.title) ?? "",
    year: Number.isInteger(year) && year > 0 ? year : null,
    status: statusOf(work.open_access),
    best: location(work.best_oa_location),
    openCopies: locations.filter(
      (entry) => (entry as { is_oa?: unknown }).is_oa === true
    ).length,
  };
}

/** Works from a `filter` response, keyed by DOI. */
export function parseOpenAlexList(body: unknown): Map<string, OaRecord> {
  const results = (body as { results?: unknown } | null)?.results;
  const records = new Map<string, OaRecord>();
  if (!Array.isArray(results)) {
    return records;
  }
  for (const raw of results) {
    const record = parseOpenAlexWork(raw);
    if (record) {
      records.set(record.doi, record);
    }
  }
  return records;
}

/** How many of each status, in the fixed order of `OA_STATUSES`. */
export function statusCounts(
  records: readonly OaRecord[]
): Record<OaStatus, number> {
  const counts = Object.fromEntries(
    OA_STATUSES.map((status) => [status, 0])
  ) as Record<OaStatus, number>;
  for (const record of records) {
    counts[record.status] += 1;
  }
  return counts;
}
